<template>
    <RegularModal ref="modal">
        <template v-slot:title>
            {{ modalTitle }}
        </template>

        <template v-slot:body>
            <div class="field" :class="{ 'has-error': !nameIsValid }">
                <label class="label">{{ translate("admin_schedules_list.schedule_name") }}:</label>
                <input type="text" class="input" v-model="scheduleName" maxlength="45" />
            </div>
            <div class="field">
                <label class="label">{{ translate("admin_schedules_list.schedule_start_time") }}:</label>
                <DateTimePicker
                    class="input"
                    enable-time="true"
                    v-model="scheduleStartTime"
                    :min-date="eventStartInEventTimezone"
                    :max-date="eventEndInEventTimezone"
                ></DateTimePicker>
            </div>
        </template>

        <template v-slot:footer>
            <div class="buttons">
                <button @click="close" class="button">
                    {{ translate("common.cancel") }}
                </button>
                <LoadingButton
                    :is-loading="saving"
                    @click="confirm"
                    class="button is-primary"
                    :disabled="!nameIsValid || !startTimeIsValid"
                >
                    {{ translate("common.ok") }}
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { computed, ref } from "vue";
    import { translate } from "../Common/translations";
    import { useModal } from "../Common/useModal";
    import RegularModal from "../Common/RegularModal.vue";
    import DateTimePicker from "../Common/DateTimePicker.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import { convertFromTimezone, convertToTimezone } from "../Common/dateTimeUtils";
    import { successToast } from "../Common/toasts";
    import { handleError } from "../Common/errorHandler";
    import { useSchedulesListingStore } from "./useSchedulesListingStore";
    import { Schedule } from "../Common/Schedule/types";

    const props = defineProps<{
        eventTimezone: string;
        eventStartDate: string;
        eventEndDate: string;
    }>();

    const store = useSchedulesListingStore();

    const { modal, open: openModal, close: closeModal } = useModal();

    const scheduleId = ref<number | null>(null);
    const scheduleName = ref("");
    const scheduleStartTime = ref("");
    const scheduleSharing = ref(0);
    const saving = ref(false);

    const modalTitle = computed(() =>
        scheduleId.value === null ? translate("admin_schedules_list.add_new_schedule") : translate("admin_schedules_list.edit"),
    );

    const eventStartInEventTimezone = computed(() => convertToTimezone(props.eventStartDate, props.eventTimezone));
    const eventEndInEventTimezone = computed(() => convertToTimezone(props.eventEndDate, props.eventTimezone));

    const nameIsValid = computed(() => {
        const name = scheduleName.value || "";

        if (name.length === 0) {
            return true;
        }

        return !!name.match(/^[^<>/"';^&$]+$/);
    });

    const startTimeIsValid = computed(() => (scheduleStartTime.value || "").length > 0);

    function confirm() {
        saving.value = true;

        store
            .saveSchedule({
                scheduleId: scheduleId.value,
                scheduleName: scheduleName.value,
                scheduleStartTime: convertFromTimezone(scheduleStartTime.value, props.eventTimezone),
                scheduleSharing: scheduleSharing.value,
            })
            .then(() => {
                successToast(translate("common.changes_have_been_saved"));
                close();
            })
            .catch(handleError)
            .finally(() => {
                saving.value = false;
            });
    }

    function open(schedule?: Schedule) {
        scheduleId.value = schedule?.id ?? null;
        scheduleName.value = schedule?.name || "";
        scheduleStartTime.value = schedule?.startTime ? convertToTimezone(schedule.startTime, props.eventTimezone) : "";
        scheduleSharing.value = schedule?.sharing || 0;
        openModal();
    }

    function close() {
        scheduleName.value = "";
        closeModal();
    }

    defineExpose({ open });
</script>
